import _ from 'lodash';
import {
  IPartnerDiscount,
  IPartnerDiscountCategories,
  IPartnerDiscountSubcategories,
  IPartnerWorkingHour,
  IPartnerWorkingHours,
  ITypesPartner,
  WeekDays,
} from 'types/Partner';
import { maskHour } from 'util/masks';

const MIN_DISCOUNT_PERCENT = 20;

export const hourStart = (dayWeek: IPartnerWorkingHour) => {
  if (!dayWeek) {
    return '';
  }
  if (!dayWeek.open) {
    return 'Fechado';
  }
  if (dayWeek.dayLong) {
    return '24h';
  }

  return maskHour(dayWeek.start);
};

export const typesPartner = (types: { [key: string]: boolean }) => {
  const typesArray = Object.keys(types || {}).filter(
    (item) => types[item as ITypesPartner] === true
  );

  const translateTypes = {
    petshop: 'Petshop',
    healthInstitution: 'Clínica ou hospital veterinário',
    sponsor: 'Patrocionador',
    others: 'Outros',
  };

  const typesPartnerText = typesArray
    .map((item) => ` - ${translateTypes[item as ITypesPartner]}`)
    .join('');

  return typesPartnerText;
};

/**
 * Check if discount has at least one item with min percent
 *
 * @param {any[]} [value]
 * @return {boolean}
 */
export const hasMinDiscountPercent = (value?: any[]): boolean => {
  const discounts = value as IPartnerDiscount[];

  const hasAtLeastOne = discounts?.some(
    (item) => item.discountValue >= MIN_DISCOUNT_PERCENT
  );

  return hasAtLeastOne;
};

/**
 * Join partner working hours value
 *
 * @param {IPartnerWorkingHours} workingHours
 * @return {string}
 *
 * // return Segunda-feira, Terça-feira...
 */
export const joinPartnerWorkingHours = (
  workingHours: IPartnerWorkingHours
): string => {
  if (!workingHours) {
    return '';
  }

  return Object.entries(workingHours)
    .filter(([_, value]: [string, IPartnerWorkingHour]) => value.open)
    .map(([key]) => WeekDays[key as keyof typeof WeekDays])
    .join(', ');
};

/**
 * Join partner discounts array as string
 *
 * @param {IPartnerDiscount[]} discounts
 * @return {*}  {string}
 */
export const joinPartnerDiscounts = (
  discounts: IPartnerDiscount[],
  showDiscount?: boolean
): string => {
  if (!discounts?.length) {
    return '';
  }

  return discounts
    .map(({ categories, discountValue }) =>
      joinPartnerDiscountCategories({
        categories,
        discount: showDiscount ? discountValue : undefined,
      })
    )
    .join('; ');
};

/**
 * Join discount subcategories values as text
 *
 * @param {IPartnerDiscountSubcategories} subcategories
 * @param {string} prefix
 * @return {*}  {string}
 */
export const joinDiscountSubcategories = (
  subcategories: IPartnerDiscountSubcategories,
  prefix: string
): string => {
  const values = _.values(subcategories);
  const selected = _.filter(values, 'isSelected');

  if (!selected.length) {
    return '';
  }

  const titles = _.map(selected, 'title').join(', ');

  return `${prefix}: ${titles}`;
};

/**
 * Join discount categories values as text
 *
 * @param {{
 *   categories: IPartnerDiscountCategories;
 *   discount?: IPartnerDiscount['discountValue'];
 *   showOthersTitle?: boolean;
 * }} {
 *   categories,
 *   discount,
 *   showOthersTitle,
 * }
 * @return {*}  {string}
 */
export const joinPartnerDiscountCategories = ({
  categories,
  discount,
  showOthersTitle,
}: {
  categories: IPartnerDiscountCategories;
  discount?: IPartnerDiscount['discountValue'];
  showOthersTitle?: boolean;
}): string => {
  if (!categories) {
    return '';
  }

  return _.entries(categories)
    .filter(([_, item]) => item?.isSelected || item?.subcategories)
    .map(([category, { isSelected, title, name, subcategories }]) => {
      const prefix = `${title}${discount ? ` (${discount}%)` : ''}`;

      if (isSelected) {
        if (!showOthersTitle && category === 'others') {
          return `${name}`;
        }

        return prefix;
      }

      if (subcategories) {
        return joinDiscountSubcategories(subcategories, prefix);
      }

      return null;
    })
    .filter((item) => item)
    .join('; ');
};

/**
 * Validate if has some category selected
 *
 * @param {(IPartnerDiscountCategories | any)} value
 * @return {*}
 */
export const validateDiscountCategories = (
  value: IPartnerDiscountCategories | any
) => {
  const categories = value as IPartnerDiscountCategories;

  return _.values(categories).some((item) => {
    if (item?.isSelected) {
      return true;
    }

    return _.some(_.values(item?.subcategories), 'isSelected');
  });
};

interface IGroupPartnerDiscounts {
  discountValue: number;
  text: string;
}

/**
 * Group discounts by discound value
 *
 * @param {IPartnerDiscount[]} discounts
 * @return {*}  {IGroupPartnerDiscounts[]}
 */
export const groupPartnerDiscounts = (
  discounts: IPartnerDiscount[]
): IGroupPartnerDiscounts[] => {
  if (!discounts?.length) {
    return [];
  }

  try {
    const grouped = _.groupBy(discounts, 'discountValue');

    return Object.entries(grouped).map(([discountValue, discountsValue]) => {
      return {
        discountValue: `${discountValue}%`,
        text: joinPartnerDiscounts(discountsValue, false),
      } as unknown as IGroupPartnerDiscounts;
    });
  } catch (reason) {
    return [];
  }
};
